import React, { useCallback, useMemo } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { IconChevronDown } from '@tabler/icons-react';

import styles from './HubFilterDropdown.module.css';

interface HubFilterOption<TValue extends string = string> {
  value: TValue;
  label: string;
}

/**
 * Resolve the effective filter, clamping a stale selection to a currently
 * available bucket so a section never renders empty after its data changes
 * (e.g. the selected bucket emptied on refetch). Falls back to the first
 * available bucket, then the first option.
 */
export function resolveAvailableFilter<TValue extends string>(
  selected: TValue | null,
  options: ReadonlyArray<{ value: TValue; available: boolean }>,
): TValue {
  if (selected !== null && options.some((o) => o.value === selected && o.available)) {
    return selected;
  }
  return (options.find((o) => o.available) ?? options[0]).value;
}

interface HubFilterDropdownProps<TValue extends string> {
  value: TValue;
  options: ReadonlyArray<HubFilterOption<TValue>>;
  onChange: (value: TValue) => void;
  ariaLabel: string;
}

/**
 * Compact filter control for Hub section headers ("Active", "Upcoming", ...).
 * Renders as a small button per the design, with an invisible native `select`
 * overlaid on top so mobile web gets the OS picker (same pattern as
 * `BuyerActionMenu`).
 */
export function HubFilterDropdown<TValue extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: HubFilterDropdownProps<TValue>) {
  const selectedLabel = useMemo(
    () => options.find((option) => option.value === value)?.label ?? '',
    [options, value],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(event.target.value as TValue);
    },
    [onChange],
  );

  return (
    <Box className={`relative shrink-0 ${styles.trigger}`}>
      {/* Visual layer only; the select below carries the accessible name/value. */}
      <Row className="items-center gap-1 rounded-lg border border-od-border-primary bg-od-bg-primary py-2 pr-2 pl-3">
        <Text as="span" className="text-label-sm text-od-content-primary">
          {selectedLabel}
        </Text>
        <IconChevronDown size={18} className="text-od-content-primary" />
      </Row>
      {/* eslint-disable-next-line @opendoor/no-html-elements -- Invisible native select keeps the OS picker experience on mobile web */}
      <select
        value={value}
        onChange={handleChange}
        aria-label={ariaLabel}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: 'pointer',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          appearance: 'none',
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Box>
  );
}
